import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { InventoriesFilters } from 'features/Inventories/views/Index/Filters'
import { IInventoriesFiltersFormValues } from 'features/Inventories/views/Index/Filters/types'
import { GET_INVENTORIES } from 'features/Inventories/queries-legacy'
import { useLogin } from 'features/Login/hooks'
import { getQueries } from 'utils/route'
import { canReadInventory, canWriteInventory } from 'features/Users/utils'
import { InventoriesList } from './List'
import { InventoriesPagination } from './Pagination'
import { filterEmptyValue } from 'utils/object'
import { GTInventoryType } from 'gql/types.generated'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'inventories.helmet',
    defaultMessage: 'Inventories | Nexbus Admin'
  }
})

const sanitizeType = (type: string): GTInventoryType | '' => {
  switch (type) {
    case GTInventoryType.Base:
    case GTInventoryType.Warehouse:
      return type
  }
  return ''
}

const InventoriesView = (): TSFixMe => {
  let content
  const location = useLocation()
  const intl = useIntl()

  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const { page = '1', name = '', type = '' } = getQueries(location.search)

  const pageNb = parseInt(page)
  const filters = {
    name,
    type: sanitizeType(type)
  }

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_INVENTORIES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: LIMIT,
      page: pageNb - 1,
      input: filterEmptyValue({
        name,
        type: filters.type
      })
    }
  })

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (!canReadInventory(login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.INVENTORY} />
  }

  const getInventoriesQuery = (
    filters: IInventoriesFiltersFormValues
  ): Record<keyof IInventoriesFiltersFormValues, string> => {
    return {
      name: filters.name,
      type: filters.type
    }
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.inventories.error.description"
            defaultMessage="Hubo un error al cargar tus ubicaciones"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="spanish.inventories.error.action" defaultMessage="Recargar tus ubicaciones" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.inventories.items) && pageNb === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.inventories.empty.description" defaultMessage="No tienes ubicaciones" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/inventories/new" kind="primary">
            <FormattedMessage id="spanish.inventories.empty.action" defaultMessage="Registra tu primera ubicación" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.inventories.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.inventories.empty.filters.description"
            defaultMessage="No se encontró ubicaciones"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/inventories" kind="primary">
            <FormattedMessage id="spanish.inventories.empty.filters.action" defaultMessage="Borrar filtros" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <InventoriesList inventories={data.inventories.items} />
        <InventoriesPagination
          page={pageNb}
          limit={LIMIT}
          totalItems={data.inventories.total}
          currentItemsCount={data.inventories.items.length}
          filters={filters}
          getInventoriesQuery={getInventoriesQuery}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="spanish.inventories.header.label" defaultMessage="Lista de ubicaciones" />
        </Heading.H3>
        {canWriteInventory(login.accessToken.policies) && (
          <Button as={NavLink} to="/inventories/new" kind="primary">
            <FormattedMessage id="spanish.inventories.header.new" defaultMessage="+ Registrar ubicación" />
          </Button>
        )}
      </ContentHeader>
      <ContentBody>
        <InventoriesFilters filters={filters} getInventoriesQuery={getInventoriesQuery} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(InventoriesView)
